'use client';

import {
  INTL_AUDIO_GENERAL_INSTRUCTIONS,
  INTL_AUDIO_PARTS,
  IntlAudioPartKey,
} from '@/lib/recruitment/intl-application';
import { AudioRecorderCard } from '@/components/recruitment/intl/AudioRecorderCard';
import { StepNav } from '@/components/recruitment/intl/steps/StepNav';

export function AudioStep({
  token,
  parts = INTL_AUDIO_PARTS,
  instructions = INTL_AUDIO_GENERAL_INSTRUCTIONS,
  audio,
  onUploaded,
  onExpired,
  onBack,
  onContinue,
}: {
  token: string;
  /** Scripts and instructions as worded in Recruitment -> Job Applications (code defaults otherwise). */
  parts?: readonly {
    key: string;
    title: string;
    instructions: string;
    scriptText: string | null;
    minSeconds: number;
    maxSeconds: number;
  }[];
  instructions?: readonly string[];
  audio: Partial<Record<IntlAudioPartKey, number | null>>;
  onUploaded: (part: IntlAudioPartKey, durationSeconds: number, completedSteps: string[]) => void;
  onExpired: () => void;
  onBack: (() => void) | null;
  onContinue: () => void;
}) {
  const allRecorded = parts.every((p) => audio[p.key as IntlAudioPartKey] != null);

  return (
    <div className="space-y-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4">
        <p className="text-sm font-semibold text-white mb-2">Before you start</p>
        <ul className="space-y-1.5">
          {instructions.map((line, i) => (
            <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />
              {line}
            </li>
          ))}
        </ul>
      </div>

      {parts.map((part) => (
        <AudioRecorderCard<IntlAudioPartKey>
          key={part.key}
          token={token}
          part={{ ...part, key: part.key as IntlAudioPartKey }}
          uploadedDurationSeconds={audio[part.key as IntlAudioPartKey] ?? null}
          onUploaded={onUploaded}
          onExpired={onExpired}
        />
      ))}

      <StepNav
        onBack={onBack}
        onContinue={onContinue}
        continueDisabled={!allRecorded}
        disabledHint="Record all three parts to continue."
      />
    </div>
  );
}
